import { JourneyPhaseSuccessPage } from "@/components/pages/SuccessPage";
import { publicFormRoutePaddingClass } from "@/features/pages/components/formPageLayout";
import { AppPageLayout } from "@/components/common/AppPageLayout";
import { useActionCopy } from "@/features/i18n/actionCopy";
import { useJourneyCopy } from "@/features/i18n/journeyCopy";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { useSelectFormById } from "@/features/forms/api/selectFormById";
import { useAppLocale } from "@/features/i18n/LocaleProvider";
import { getMergedPublicFormEntryValues } from "@/features/forms/utils/publicFormEntryValuesMerge";
import { useEntryValuesStore } from "@/features/inputs/hooks/useEntryValuesStore";
import { submitFormResults } from "@/features/forms/api/submitFormResults";
import { EmailGateDialog } from "@patient-watch/forms";
import { useMutation } from "@tanstack/react-query";
import { mapEntryValuesViewRows } from "@/features/entries/api/selectEntryValuesByEntryId";
import { trackEvent } from "@/utils/trackEvent";
import { toast } from "@patient-watch/ui/sonner";
import { useToastCopy } from "@/features/i18n/toastCopy";
import {
    submitValidatorFormReview,
    validatorReviewAnswers,
} from "@/features/validations/utils/submitValidatorFormReview";
import {
    consumeValidatorAutoSubmitEmail,
    readValidatorFormReview,
} from "@/features/validations/utils/validatorAutoSubmitEmail";
import { useProfileByIdQuerySafe } from "@/features/profile/api/selectProfile";
import { useAuth } from "@/utils/auth";
import Spinner from "@patient-watch/ui/spinner";

const RESULTS_STORAGE_KEY = (formId: string, entryId: string) => `pw_public_form_results:${formId}:${entryId}`;
const clinicianEmailStorageKey = (formId: string) => `pw_public_form_clinician_email:${formId}`;

function ResultsPlaceholderPreview({ formName }: { formName?: string }) {
    const bars = [85, 62, 74, 50, 90, 68];
    return (
        <div className="space-y-3">
            <p className="font-bold text-indigo-900 text-base">{formName ?? "Form"}</p>
            <div className="space-y-2">
                {bars.map((w, i) => (
                    <div key={i} className="flex items-center gap-3">
                        <div className="bg-indigo-200/40 rounded h-2.5" style={{ width: `${40 + i * 5}%` }} />
                        <div className="bg-slate-200/60 rounded h-2.5" style={{ width: `${w * 0.3}%` }} />
                    </div>
                ))}
            </div>
        </div>
    );
}

/** The patient form-complete screen, without scores, for a validator screen review. */
function ValidatorThankYou() {
    const journeyCopy = useJourneyCopy();
    const actionCopy = useActionCopy();

    return (
        <AppPageLayout width="md" padding="sm" className={publicFormRoutePaddingClass}>
            <div data-testid="form-thank-you">
                <JourneyPhaseSuccessPage
                    title={journeyCopy.formCompleteTitle}
                    statusLabel={journeyCopy.formCompleteTitle}
                    description={journeyCopy.formCompleteNextDescription}
                    isLoading={false}
                    minLoadingTime={0}
                    pinFooter
                    primaryButton={{
                        text: actionCopy.done,
                        to: "/validate",
                    }}
                />
            </div>
        </AppPageLayout>
    );
}

const CompleteGate = () => {
    const toastCopy = useToastCopy();
    const { formId, diaryId, trackerId, entryId } = Route.useParams();
    const navigate = useNavigate();
    const { locale } = useAppLocale();
    const auth = useAuth();
    const { data: form } = useSelectFormById(formId, locale);
    const { data: signedInProfile, isPending: isSignedInProfilePending } = useProfileByIdQuerySafe(
        auth.isAuthenticated ? auth.id : null
    );
    const getEntryValues = useEntryValuesStore((s) => s.getEntryValues);
    const [validatorAutoSubmitEmail] = useState(() => consumeValidatorAutoSubmitEmail());
    const [validatorReview] = useState(() => readValidatorFormReview());
    const activeReview = validatorReview?.formId === formId ? validatorReview : null;
    const signedInEmail = signedInProfile?.email?.trim() || null;
    const autoSubmitEmail = activeReview ? signedInEmail : validatorAutoSubmitEmail || signedInEmail;
    const isResolvingSignedInEmail =
        Boolean(activeReview) || (!validatorAutoSubmitEmail && auth.isAuthenticated && isSignedInProfilePending);
    const autoSubmitTriggeredRef = useRef(false);

    useEffect(() => {
        if (autoSubmitEmail || isResolvingSignedInEmail) return;
        trackEvent("email_gate_view", { form_id: formId });
    }, [formId, autoSubmitEmail, isResolvingSignedInEmail]);

    const submitMutation = useMutation({
        mutationFn: submitFormResults,
        onSuccess: (data) => {
            const entryValues = mapEntryValuesViewRows(data.entryValues ?? []);
            try {
                sessionStorage.setItem(
                    RESULTS_STORAGE_KEY(formId, entryId),
                    JSON.stringify({
                        entryValues,
                        diaryId: data.diaryId,
                        formId,
                    })
                );
                trackEvent("email_gate_submit", { form_id: formId, diary_id: data.diaryId });
                if (data.emailSent === true) {
                    toast.success(toastCopy.resultsSentToEmail);
                } else {
                    toast.warning(toastCopy.resultsEmailSendFailed);
                }
                void navigate({
                    to: "/forms/$formId/diary/$diaryId/tracker/$trackerId/entry/$entryId/complete/results",
                    params: { formId, diaryId, trackerId, entryId },
                });
            } catch (e) {
                console.error("Failed to store results:", e);
                toast.error(toastCopy.somethingWentWrong);
            }
        },
        onError: () => {
            toast.error(toastCopy.somethingWentWrong);
        },
    });

    const handleEmailGateSubmit = async (email: string) => {
        try {
            sessionStorage.setItem(clinicianEmailStorageKey(formId), email);
        } catch {
            /* ignore */
        }
        const storeValues = getMergedPublicFormEntryValues(entryId, getEntryValues());
        submitMutation.mutate({
            formId,
            email,
            entryValues: storeValues.map((ev) => ({
                entry_id: entryId,
                input_id: ev.input_id,
                text_value: ev.text_value,
                number_value: ev.number_value,
                date_value: ev.date_value as string | null | undefined,
                text_array: ev.text_array,
                object_id: ev.object_id,
                json_value: ev.json_value as Record<string, unknown> | null | undefined,
                entry_value_id: ev.entry_value_id,
            })),
        });
    };

    // A screen-review flag with no validation id still shows the thank-you screen.
    // A validation review leaves this public complete screen for the validation page.
    useEffect(() => {
        if (!activeReview || autoSubmitTriggeredRef.current) return;
        const email = activeReview.email || signedInEmail;
        if (!email) {
            if (isSignedInProfilePending) return;
            void navigate({
                to: "/validate/$validationId/forms/$formId",
                params: { validationId: activeReview.validationId, formId },
            });
            return;
        }
        autoSubmitTriggeredRef.current = true;
        const answers = validatorReviewAnswers(entryId, getEntryValues());
        void submitValidatorFormReview({
            validationId: activeReview.validationId,
            formId,
            entryId,
            email,
            entryValues: answers,
        })
            .then((validationId) => {
                void navigate({
                    to: "/validate/$validationId/forms/$formId/results",
                    params: { validationId, formId },
                });
            })
            .catch(() => {
                void navigate({
                    to: "/validate/$validationId/forms/$formId",
                    params: { validationId: activeReview.validationId, formId },
                });
            });
    }, [activeReview, signedInEmail, isSignedInProfilePending, entryId, formId, getEntryValues, navigate]);

    useEffect(() => {
        if (activeReview || validatorAutoSubmitEmail || !autoSubmitEmail || autoSubmitTriggeredRef.current) return;
        autoSubmitTriggeredRef.current = true;
        void handleEmailGateSubmit(autoSubmitEmail);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [autoSubmitEmail]);

    const localEntryValues = getMergedPublicFormEntryValues(entryId, getEntryValues());
    const previewEntryValues = localEntryValues as Array<
        (typeof localEntryValues)[number] & {
            input_index?: number | null;
            input_label?: string | null;
            page_index?: number | null;
            value?: unknown;
        }
    >;

    if (activeReview) {
        return (
            <section className="flex flex-col justify-center items-center gap-3 py-24">
                <Spinner className="w-10 h-10" />
                <p className="text-slate-500 text-sm">Saving your answers...</p>
            </section>
        );
    }

    if (validatorAutoSubmitEmail) {
        return <ValidatorThankYou />;
    }

    if ((autoSubmitEmail || isResolvingSignedInEmail) && !submitMutation.isError) {
        return (
            <section className="flex flex-col justify-center items-center gap-3 py-24">
                <Spinner className="w-10 h-10" />
                <p className="text-slate-500 text-sm">Calculating your results...</p>
            </section>
        );
    }

    return (
        <section
            className="box-border pb-[max(2rem,env(safe-area-inset-bottom))] flex flex-col mx-auto w-full min-w-0 max-w-lg notranslate"
            translate="no"
        >
            <EmailGateDialog
                open
                formName={form?.form_name}
                isSubmitting={submitMutation.isPending}
                onEmailSubmitted={handleEmailGateSubmit}
                resultsPreview={
                    previewEntryValues.length > 0 ? (
                        <div className="space-y-3">
                            <p className="font-bold text-indigo-900 text-base">{form?.form_name}</p>
                            <dl className="space-y-2">
                                {previewEntryValues
                                    .filter((ev) => ev.input_index === 0 || ev.page_index != null)
                                    .slice(0, 8)
                                    .map((ev) => (
                                        <div
                                            key={ev.input_id}
                                            className="flex justify-between items-center gap-3 min-w-0"
                                        >
                                            <dt className="min-w-0 text-slate-600 text-sm truncate">
                                                {ev.input_label ?? ev.input_id}
                                            </dt>
                                            <dd className="bg-slate-100 px-2 py-0.5 rounded font-semibold text-slate-900 text-sm text-right shrink-0">
                                                {ev.number_value != null && ev.text_value
                                                    ? `${ev.number_value} - ${ev.text_value}`
                                                    : (ev.number_value ?? ev.text_value ?? String(ev.value ?? "-"))}
                                            </dd>
                                        </div>
                                    ))}
                            </dl>
                        </div>
                    ) : (
                        <ResultsPlaceholderPreview formName={form?.form_name} />
                    )
                }
            />
        </section>
    );
};

export const Route = createFileRoute("/forms/$formId/diary/$diaryId/tracker/$trackerId/entry/$entryId/complete/")({
    component: CompleteGate,
});
